/**
 * 角色头像上传组件
 * 
 * v11.2.2 新增：发布项目到 App 前需要上传角色形象图片
 * 使用 S3 Presigned URL 方式上传
 */

import { useState, useRef, useCallback } from 'react'
import { Upload, Loader2, X, ImageIcon } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { ProjectPublishApiService, uploadAvatarToS3 } from '../services/app-api'

interface AvatarUploadProps {
  /** 项目 ID（用于组织存储路径） */
  projectId: string
  /** 当前 avatar_url */
  value?: string
  /** URL 变更回调 */
  onChange: (url: string) => void
  /** 上传开始回调 */
  onUploadStart?: () => void
  /** 上传结束回调 */
  onUploadEnd?: () => void
  /** 上传出错回调 */
  onError?: (message: string) => void
  /** 自定义类名 */
  className?: string
  /** 是否禁用 */
  disabled?: boolean
}

export default function AvatarUpload({
  projectId,
  value,
  onChange,
  onUploadStart,
  onUploadEnd,
  onError,
  className,
  disabled = false,
}: AvatarUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 处理文件选择
  const handleFileSelect = useCallback(async (file: File) => {
    // 验证文件类型
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      onError?.('请上传 PNG、JPG 或 WebP 格式的图片')
      return
    }

    // 验证文件大小（最大 5MB）
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      onError?.('图片大小不能超过 5MB')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)
    onUploadStart?.()

    try {
      // 1. 获取预签名上传 URL
      setUploadProgress(10)
      const response = await ProjectPublishApiService.getAvatarUploadUrl({
        project_id: projectId,
        file_name: file.name,
        content_type: file.type,
      })

      if (!response.success || !response.data) {
        throw new Error('获取上传 URL 失败')
      }

      const { upload_url, avatar_url } = response.data

      // 2. 上传到 S3
      setUploadProgress(30)
      await uploadAvatarToS3(upload_url, file)

      // 3. 等待 Lambda 处理（模拟进度）
      setUploadProgress(60)
      
      // 轮询检查图片是否处理完成（最多等待 10 秒）
      let processed = false
      const maxAttempts = 10
      for (let i = 0; i < maxAttempts; i++) {
        setUploadProgress(60 + (i * 4)) // 60-100
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // 尝试加载图片检查是否可用
        try {
          const img = new Image()
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve()
            img.onerror = () => reject()
            img.src = avatar_url + '?t=' + Date.now() // 加时间戳避免缓存
          })
          processed = true
          break
        } catch {
          // 图片还未处理完成，继续等待
        }
      }

      if (!processed) {
        // 即使无法验证，也返回 URL（Lambda 可能还在处理）
        console.warn('⚠️ [AvatarUpload] 无法验证图片处理完成，但仍返回 URL')
      }

      setUploadProgress(100)
      onChange(avatar_url)

    } catch (error) {
      console.error('❌ [AvatarUpload] 上传失败:', error)
      onError?.(error instanceof Error ? error.message : '上传失败，请重试')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
      onUploadEnd?.()
    }
  }, [projectId, onChange, onUploadStart, onUploadEnd, onError])

  // 处理文件输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    // 重置 input，允许重复选择同一文件
    e.target.value = ''
  }

  // 处理拖拽
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    if (disabled || isUploading) return
    
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  // 点击上传区域
  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }

  // 清除已上传图片
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
  }

  return (
    <div className={cn('relative', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative w-full aspect-[9/16] rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden',
          'flex flex-col items-center justify-center',
          isDragOver && 'border-primary bg-primary/5',
          !isDragOver && !value && 'border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50',
          value && 'border-transparent',
          (disabled || isUploading) && 'cursor-not-allowed opacity-60',
        )}
      >
        {value ? (
          // 已上传图片预览
          <>
            <img
              src={value}
              alt="角色形象"
              className="w-full h-full object-cover"
            />
            {/* 悬浮操作层 */}
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClick}
                disabled={disabled || isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                更换图片
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={disabled || isUploading}
                className="text-white hover:text-white hover:bg-white/20"
              >
                <X className="h-4 w-4 mr-2" />
                移除
              </Button>
            </div>
          </>
        ) : isUploading ? (
          // 上传中
          <div className="flex flex-col items-center gap-3 p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-sm text-muted-foreground">
              上传中 {uploadProgress}%
            </div>
            {/* 进度条 */}
            <div className="w-32 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          // 空状态
          <div className="flex flex-col items-center gap-3 p-4 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">上传角色形象</p>
              <p className="text-xs text-muted-foreground mt-1">
                建议尺寸 1080×1920
              </p>
              <p className="text-xs text-muted-foreground">
                支持 PNG、JPG、WebP
              </p>
            </div>
            <Button variant="outline" size="sm" className="mt-2">
              <Upload className="h-4 w-4 mr-2" />
              选择图片
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
